import React, {Fragment} from "react";
import {isEmpty} from "lodash";
import {Col, Row} from "reactstrap";
import {useSelector} from "react-redux";
import {useMediaQuery} from "react-responsive";
import {useDisclosure} from "../../hooks/useDisclosure";
import Button from "../../components/Button";
import SelectCreator from "../../components/Forms/components/SelectCreator";
import SafeView from "../../components/safeView/SafeView";
import EmptyGraphMessage from "../../components/Charts/Containers/EmptyGraphMessage";
import RangePicker from "../../components/RangePicker";
import Cells from "../../components/Charts/Containers/Cell/Cells";
import {getDataExplorerSensor} from "../../redux/Selectors/data-explorer.selectors";
import {useHandleTimeRange} from "../../views/Dashboard/useHandleTimeRange";
import {useCleanup, useHandleForm} from "./dataExplorer.hooks";
import SaveCell from "./SaveCell";
import "./dataExplorer.scss";

const DataExplorerPage = () => {
  const sensor = useSelector(getDataExplorerSensor);
  const isDesktop = useMediaQuery({query: "(min-width: 1080px)"});
  const {
    isOpen: isRangePickerOpen,
    onOpen: openRangePicker,
    onClose: closeRangePicker,
  } = useDisclosure();
  const {
    isOpen: isSaveCellOpen,
    onOpen: openSaveCell,
    onClose: closeSaveCell,
  } = useDisclosure();

  useCleanup();
  const {
    timeRangeOptions,
    timeRangeStart,
    timeRangeStop,
    windowPeriod,
    handleTimeRangeChange,
    handleTimeRangeSave,
  } = useHandleTimeRange(openRangePicker);
  const {
    analysis,
    handleTemplateChange,
    handleInputSensorChange,
    handleInputSensor2Change,
    inputSensor,
    inputSensor2,
    inputSensorOptions,
    inputSensorOptions2,
    setTimezone,
    template,
    templateOptions,
    timezone,
    timezoneOptions,
  } = useHandleForm();

  const isTotalizerTemplate =
    template === "get_totalizer_weekly" || template === "get_totalizer_monthly";

  return (
    <SafeView>
      <div className="data--explorer--container">
        {isSaveCellOpen && (
          <SaveCell
            open={isSaveCellOpen}
            toggle={closeSaveCell}
            sensor={sensor}
            template={template}
            inputSensor={inputSensor}
            inputSensor2={inputSensor2}
          />
        )}
        {isRangePickerOpen && (
          <RangePicker
            open={isRangePickerOpen}
            onCloseHandler={closeRangePicker}
            handleSave={handleTimeRangeSave}
            timeZone={timezone}
          />
        )}
        <h1>Data Explorer {analysis.name || ""}</h1>
        <div className="d-flex flex-column flex-md-row mb-2 mb-md-0">
          <Row className="flex-grow-1">
            <Col
              sm="6"
              lg="4"
              xl="3"
              className="ml-md--3"
              style={{maxWidth: `${isDesktop ? "230px" : "100%"}`}}
            >
              <SelectCreator
                id="template"
                colSize={12}
                value={template}
                placeholder={"Choose a template"}
                name="template"
                options={templateOptions}
                onChange={(e) => handleTemplateChange(e.target.value)}
                isSearchable
              />
            </Col>
            <Col
              sm="6"
              lg="4"
              xl="3"
              className="ml-md--3"
              style={{maxWidth: `${isDesktop ? "230px" : "100%"}`}}
            >
              <SelectCreator
                id="timezone"
                colSize={12}
                value={timezone}
                placeholder={"Choose a timezone"}
                name="timezone"
                options={timezoneOptions}
                onChange={(e) => setTimezone(e.target.value)}
                isSearchable
              />
            </Col>
            {!isTotalizerTemplate && (
              <Col
                sm="12"
                lg="4"
                xl="3"
                className="ml-md--3 pr-md-4"
                style={{
                  maxWidth: `${isDesktop ? (timeRangeStop ? "400px" : "260px") : "100%"}`,
                }}
              >
                <SelectCreator
                  id="timeRangeStart"
                  colSize={12}
                  placeholder={"Look-back period"}
                  name="timeRangeStart"
                  options={timeRangeOptions}
                  value={timeRangeStart}
                  onChange={handleTimeRangeChange}
                  isSearchable
                />
              </Col>
            )}
            {(template === "dual_graph" || template === "triple_graph") && (
              <Fragment>
                <Col
                  sm="6"
                  lg="4"
                  xl="3"
                  className="ml-md--3"
                  style={{maxWidth: `${isDesktop ? "230px" : "100%"}`}}
                >
                  <SelectCreator
                    id="inputSensor"
                    colSize={12}
                    placeholder={"Select input"}
                    name="inputSensor"
                    options={inputSensorOptions}
                    value={inputSensor}
                    onChange={(e) => handleInputSensorChange(e.target.value)}
                  />
                </Col>
                {template === "triple_graph" && (
                  <Col
                    sm="6"
                    lg="4"
                    xl="3"
                    className="ml-md--3"
                    style={{maxWidth: `${isDesktop ? "230px" : "100%"}`}}
                  >
                    <SelectCreator
                      id="inputSensor2"
                      colSize={12}
                      placeholder={"Select second input"}
                      name="inputSensor2"
                      options={inputSensorOptions2}
                      value={inputSensor2}
                      onChange={(e) => handleInputSensor2Change(e.target.value)}
                    />
                  </Col>
                )}
              </Fragment>
            )}
          </Row>
          <div className="d-flex justify-content-end">
            <Button
              data-cy={"save_analysis"}
              onClick={openSaveCell}
              style={{
                display: "flex",
                maxHeight: "38px",
                alignItems: "center",
              }}
              type="default"
              withShadow
              withRadius
            >
              Save As
            </Button>
          </div>
        </div>

        {!isEmpty(analysis) ? (
          <Cells
            dashboardId={"data-explorer"}
            cells={[analysis]}
            isDraggable={false}
            isResizable={false}
            timeZone={timezone}
            timeRangeStart={timeRangeStart}
            timeRangeStop={timeRangeStop}
            windowPeriod={isTotalizerTemplate ? undefined : windowPeriod}
          />
        ) : (
          <div style={{height: "500px"}}>
            <EmptyGraphMessage message="This Dashboard doesn't have any cells" />
          </div>
        )}
      </div>
    </SafeView>
  );
};

export default DataExplorerPage;
